import { useCallback, useEffect, useMemo, useState } from "react";
import { MarketplaceAccountsTable } from "../components/listing/desktop/MarketplaceAccountsTable";
import { MarketplaceAccountCard } from "../components/listing/mobile/MarketplaceAccountCard";
import { MarketplaceConnectModal } from "../components/listing/shared/MarketplaceConnectModal";
import { MarketplaceAccountsToolbar } from "../components/listing/shared/MarketplaceAccountsToolbar";
import { MarketplacePagination } from "../components/listing/shared/MarketplacePagination";
import { useMarketplaceAccounts } from "../hooks/useMarketplaceAccounts";
import ContentHeader from "../../../../layouts/content/ContentHeader";
import { MARKETPLACE_TYPES } from "../types/marketplaceAccount";

export function MarketplaceSettingsPage() {
  const { clientId, state, items, total, loading, error, refresh, updateState } = useMarketplaceAccounts();
  const [connectOpen, setConnectOpen] = useState(false);
  const marketplaceTypes = useMemo(() => [...MARKETPLACE_TYPES], []);
  const handleSearch = useCallback((search: string) => updateState({ search }), [updateState]);

  useEffect(() => {
    const handlePopupMessage = (event: MessageEvent) => {
      if (event.data === "ServerClosed" || (typeof event.data === "string" && event.data.includes("reconnectID"))) refresh();
    };
    window.addEventListener("message", handlePopupMessage);
    return () => window.removeEventListener("message", handlePopupMessage);
  }, [refresh]);

  return (
    <section className="tw-mx-auto tw-flex tw-h-full tw-min-h-0 tw-w-full tw-max-w-7xl tw-flex-col tw-px-4 tw-py-5 sm:tw-px-6 lg:tw-px-8">
      <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-overflow-hidden tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white dark:tw-border-slate-700 dark:tw-bg-slate-900">
        <ContentHeader
          className="tw-shrink-0"
          title="Marketplace Accounts"
          searchValue={state.search}
          searchPlaceholder="Search shops or shop IDs"
          onSearchChange={handleSearch}
          onSearchSubmit={handleSearch}
          buttons={[
            {
              text: "Refresh",
              onClick: refresh,
              variant: "secondary",
              icon: "refresh",
              disabled: loading,
              "data-testid": "refresh-marketplace-accounts-button",
            },
            {
              text: "Connect marketplace",
              onClick: () => setConnectOpen(true),
              variant: "primary",
              icon: "add",
              "data-testid": "connect-marketplace-button",
            },
          ]}
        />
        <MarketplaceAccountsToolbar
          activity={state.activity}
          selectedMarketplaceTypes={state.marketplaceTypes}
          marketplaceTypes={marketplaceTypes}
          onActivityChange={(activity) => updateState({ activity })}
          onMarketplaceTypesChange={(marketplaceTypes) => updateState({ marketplaceTypes })}
        />
        {!clientId ? <div className="tw-flex-1 tw-p-10 tw-text-center tw-text-sm tw-text-slate-500">Select a client to view marketplace accounts.</div> : <>
          <div className="tw-hidden tw-min-h-0 tw-flex-1 tw-overflow-y-auto md:tw-flex md:tw-flex-col"><MarketplaceAccountsTable accounts={items} loading={loading} error={error} onRetry={refresh} onChanged={refresh} /></div>
          <div className="tw-block tw-min-h-0 tw-flex-1 tw-overflow-y-auto md:tw-hidden">
            {error ? <div className="tw-p-8 tw-text-center"><p className="tw-text-sm tw-text-red-600">{error.message}</p><button type="button" onClick={refresh} className="tw-mt-3 tw-text-sm tw-font-semibold tw-text-primary">Try again</button></div> : loading ? <div className="tw-space-y-3 tw-p-4">{Array.from({ length: 4 }, (_, index) => <div key={index} className="tw-h-28 tw-animate-pulse tw-rounded-md tw-bg-slate-100 dark:tw-bg-slate-800" />)}</div> : items.length ? items.map((account) => <MarketplaceAccountCard key={account.id} account={account} onChanged={refresh} />) : <div className="tw-p-12 tw-text-center tw-text-sm tw-text-slate-500">No marketplace accounts match the current filters.</div>}
          </div>
          <div className="tw-shrink-0"><MarketplacePagination currentPage={state.page} pageSize={state.pageSize} total={total} onPageChange={(page) => updateState({ page })} onPageSizeChange={(pageSize) => updateState({ pageSize })} /></div>
        </>}
      </div>
      <MarketplaceConnectModal isOpen={connectOpen} onClose={() => setConnectOpen(false)} onConnected={refresh} />
    </section>
  );
}
